<template lang="pug">
.svg-viewer
  .svg-viewport(
    ref="viewport"
    @wheel.prevent="onWheel"
    @mousedown="startDrag"
    @mousemove="onDrag"
    @mouseup="endDrag"
    @mouseleave="endDrag"
    @touchstart.prevent="startDrag"
    @touchmove.prevent="onDrag"
    @touchend.prevent="endDrag"
    @dblclick="onDoubleClick"
    @click="onSvgClick"
  )
    div.svg-content(ref="svgContainer")

  transition(name="toast-fade")
    .error-toast(v-if="errorMessage") {{ errorMessage }}

  .minimap(
    ref="minimapWrapper"
    :style="{ aspectRatio: worldWidth + ' / ' + worldHeight }"
    @mousedown="startMinimapDrag"
    @touchstart.prevent="startMinimapDrag"
  )
    svg.minimap-svg(:viewBox="worldViewBox" ref="minimapSvg")
      g(ref="minimapContent")
      rect.minimap-viewport-rect(
        :x="x"
        :y="y"
        :width="width"
        :height="height"
        :rx="minimapCornerRadius"
        :ry="minimapCornerRadius"
      )
  .control-pad
    .pad-directions
      button.pad-btn.up(
        type="button"
        @mousedown="startHold(() => pan(0, -1))"
        @mouseup="endHold"
        @mouseleave="endHold"
        @touchstart.prevent="startHold(() => pan(0, -1))"
        @touchend.prevent="endHold"
      ) ▲
      button.pad-btn.left(
        type="button"
        @mousedown="startHold(() => pan(-1, 0))"
        @mouseup="endHold"
        @mouseleave="endHold"
        @touchstart.prevent="startHold(() => pan(-1, 0))"
        @touchend.prevent="endHold"
      ) ◀
      button.pad-btn.center(type="button")
      button.pad-btn.right(
        type="button"
        @mousedown="startHold(() => pan(1, 0))"
        @mouseup="endHold"
        @mouseleave="endHold"
        @touchstart.prevent="startHold(() => pan(1, 0))"
        @touchend.prevent="endHold"
      ) ▶
      button.pad-btn.down(
        type="button"
        @mousedown="startHold(() => pan(0, 1))"
        @mouseup="endHold"
        @mouseleave="endHold"
        @touchstart.prevent="startHold(() => pan(0, 1))"
        @touchend.prevent="endHold"
      ) ▼
    .pad-zoom
      button.zoom-btn(
        type="button"
        @mousedown="startHold(() => zoom(-1))"
        @mouseup="endHold"
        @mouseleave="endHold"
        @touchstart.prevent="startHold(() => zoom(-1))"
        @touchend.prevent="endHold"
      ) +
      button.zoom-btn(
        type="button"
        @mousedown="startHold(() => zoom(1))"
        @mouseup="endHold"
        @mouseleave="endHold"
        @touchstart.prevent="startHold(() => zoom(1))"
        @touchend.prevent="endHold"
      ) −
</template>

<script>
import zoomPanMixin from './mixins/zoomPanMixin'
import hierarchyMixin from './mixins/hierarchyMixin'
import adjacencyMixin from './mixins/adjacencyMixin'
import selectionMixin from './mixins/selectionMixin'
import autoSelectMixin from './mixins/autoSelectMixin'
import minimapMixin from './mixins/minimapMixin'
import errorToastMixin from './mixins/errorToastMixin'
import holdRepeatMixin from './mixins/holdRepeatMixin'

export default {
  name: 'ZoomableSvg',
  mixins: [
    zoomPanMixin,
    hierarchyMixin,
    adjacencyMixin,
    selectionMixin,
    autoSelectMixin,
    minimapMixin,
    errorToastMixin,
    holdRepeatMixin
  ],
  props: {
    svgUrl: { type: String, default: '/test.svg' },
    initialCategory: { type: String, default: 'K1' },
    modelValue: { type: Array, default: () => [] },
    unavailableIds: { type: Array, default: () => [] },
    selectionColor: { type: String, default: 'black' },

    minScale: { type: Number, default: 0.2 },
    maxScale: { type: Number, default: 1 },
    panStep: { type: Number, default: 40 },
    zoomStep: { type: Number, default: 0.12 },
    dblClickZoomStep: { type: Number, default: 0.3 },
    easeFactor: { type: Number, default: 0.18 },
    wheelThreshold: { type: Number, default: 40 },
    wheelCooldown: { type: Number, default: 80 },
    holdRepeatDelay: { type: Number, default: 300 },
    holdRepeatInterval: { type: Number, default: 80 },

    doubleClickZoomEnabled: { type: Boolean, default: true },
    pinchZoomEnabled: { type: Boolean, default: true },

    ruleBlockUnavailable: { type: Boolean, default: true },
    ruleMustBeAdjacent: { type: Boolean, default: true },
    ruleMustStayConnected: { type: Boolean, default: true },
    ruleMustStartAtEdge: { type: Boolean, default: true },
    ruleTischUebergreifendErlaubt: { type: Boolean, default: true },
    ruleTafelUebergreifendErlaubt: { type: Boolean, default: false },
    wheelZoomEnabled: { type: Boolean, default: true },
    maxCount: { type: Number,  default: null }
  },
  emits: ['update:modelValue'],
  expose: ['selectBestGroup', 'clearSelection', 'deselectElementById', 'setUnavailableIds'],
  async mounted() {
    await this.loadSvg()
  },
  methods: {
    async loadSvg() {
      try {
        const response = await fetch(this.svgUrl)
        const svgText = await response.text()

        this.$refs.svgContainer.innerHTML = svgText

        const svgEl = this.$refs.svgContainer.querySelector('svg')
        if (!svgEl) {
          this.showError('Der Sitzplan konnte nicht geladen werden.')
          return
        }

        this.svgRoot = svgEl
        svgEl.classList.add('svg-content-inner')

        const vb = svgEl.getAttribute('viewBox')
        if (vb) {
          const [, , w, h] = vb.split(/\s+/).map(Number)
          if (w && h) {
            this.worldWidth = w
            this.worldHeight = h
            this.width = w
            this.height = h
            this.targetWidth = w
            this.targetHeight = h
          }
        }

        svgEl.setAttribute('viewBox', this.viewBox)
        svgEl.removeAttribute('width')
        svgEl.removeAttribute('height')

        this.initHierarchy()
        this.applyAvailability()
        this.renderMinimap()
      } catch (err) {
        this.showError('Der Sitzplan konnte nicht geladen werden.')
      }
    }
  }
}
</script>

<style scoped>
.svg-viewer,
.control-pad,
.minimap {
  user-select: none;
  -webkit-user-select: none;
  -webkit-touch-callout: none;
}

.svg-viewer {
  position: relative;
  width: 100%;
  height: 100%;
}

.svg-viewport {
  width: 100%;
  height: 100%;
  overflow: hidden;
  touch-action: none;
  cursor: grab;
}
.svg-viewport:active {
  cursor: grabbing;
}

.svg-content {
  width: 100%;
  height: 100%;
}
.svg-content :deep(.svg-content-inner) {
  width: 100%;
  height: 100%;
  display: block;
}
.svg-content :deep([id]) {
  cursor: pointer;
}
.svg-content :deep(#stuff),
.svg-content :deep(#stuff *) {
  cursor: grab;
}
.svg-content :deep(.unavailable) {
  fill: #ccc;
  cursor: not-allowed !important;
}

.error-toast {
  position: absolute;
  top: clamp(10px, 2.5vw, 20px);
  left: 50%;
  transform: translateX(-50%);
  background: #d32f2f;
  color: white;
  padding: 10px 18px;
  border-radius: 8px;
  font-size: clamp(12px, 2.5vw, 14px);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
  z-index: 10;
  max-width: 80%;
  text-align: center;
  pointer-events: none;
}
.toast-fade-enter-active,
.toast-fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}
.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, -10px);
}

.minimap {
  position: absolute;
  top: clamp(10px, 2.5vw, 20px);
  right: clamp(10px, 2.5vw, 20px);
  width: clamp(90px, 20vw, 150px);
  height: auto;
  background: #262626;
  border: 2px solid var(--color--primary);
  border-radius: 8px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.6);
  overflow: hidden;
  cursor: grab;
  touch-action: none;
}
.minimap.is-dragging {
  cursor: grabbing;
}
.minimap-svg {
  width: 100%;
  height: 100%;
  display: block;
}
.minimap-svg :deep(.unavailable) {
  fill: #555;
}
.minimap-viewport-rect {
  fill: rgba(242, 180, 0, 0.15);
  stroke: var(--color--primary);
  stroke-width: 4;
  vector-effect: non-scaling-stroke;
  pointer-events: none;
}

.control-pad {
  position: absolute;
  bottom: clamp(10px, 2.5vw, 20px);
  right: clamp(10px, 2.5vw, 20px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
}
.pad-directions {
  display: grid;
  grid-template-columns: repeat(3, clamp(22px, 6vw, 28px));
  grid-template-rows: repeat(3, clamp(22px, 6vw, 28px));
  gap: 2px;
  padding: 3px;
  background: var(--color--primary);
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
  box-sizing: content-box;
}
.pad-btn,
.zoom-btn {
  box-sizing: border-box;
  border: none;
  background: transparent;
  color: #ffffff;
  padding: 0;
  cursor: pointer;
  border-radius: 50%;
  transition: background 0.2s ease, transform 0.15s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pad-btn:hover,
.zoom-btn:hover {
  background: rgba(255, 255, 255, 0.3);
}
.pad-btn:active,
.zoom-btn:active {
  transform: scale(0.85);
  background: rgba(255, 255, 255, 0.45);
}
.pad-btn {
  width: clamp(22px, 6vw, 28px);
  height: clamp(22px, 6vw, 28px);
  font-size: clamp(9px, 2.2vw, 11px);
}
.pad-btn.center { cursor: default; pointer-events: none; }

.up { grid-column: 2; grid-row: 1; }
.left { grid-column: 1; grid-row: 2; }
.right { grid-column: 3; grid-row: 2; }
.down { grid-column: 2; grid-row: 3; }
.center { grid-column: 2; grid-row: 2; }

.pad-zoom {
  display: flex;
  gap: 4px;
  padding: 3px;
  background: var(--color--primary);
  border-radius: 999px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.4);
  box-sizing: content-box;
}
.zoom-btn {
  width: clamp(22px, 6vw, 28px);
  height: clamp(22px, 6vw, 28px);
  font-size: clamp(11px, 2.6vw, 13px);
  font-weight: 600;
  line-height: 1;
  flex-shrink: 0;
}

.batch-select {
  position: absolute;
  top: clamp(10px, 2.5vw, 20px);
  left: clamp(10px, 2.5vw, 20px);
  display: flex;
  gap: 6px;
}
</style>
